import { Direction } from '@angular/cdk/bidi';
import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatOption, MatOptionSelectionChange } from '@angular/material/core';
import {
  FloatLabelType,
  MatFormFieldAppearance,
} from '@angular/material/form-field';
import { IconType } from '@ng-icons/core';
import {
  Observable,
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import {
  MOT_OVERLAY_PANEL_CLOSED,
  MOT_OVERLAY_PANEL_OPENED,
} from 'src/app/@shared/constants/overlay-panel-a11y.constants';
import { focusMatOptionElement } from '../../utils/focus-mat-option.util';
import {
  attachMatOptionListPanelKeyboard,
  detachMatOptionListPanelKeyboard,
  getFocusedMatOptionIndex,
  MatOptionListPanelBinding,
} from '../../utils/mat-option-list-keyboard.util';

@Component({
    selector: 'mot-autocomplete',
    templateUrl: './mot-autocomplete.component.html',
    styleUrls: ['./mot-autocomplete.component.scss'],
    standalone: false
})
export class MotAutocompleteComponent<T>
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('motAutocompleteInput')
  private motAutocompleteInput?: ElementRef<HTMLInputElement>;

  @ViewChild(MatAutocompleteTrigger)
  private autocompleteTrigger?: MatAutocompleteTrigger;

  @ViewChild('auto')
  private matAutocomplete?: MatAutocomplete;

  private activateFirstOptionTimeout: ReturnType<typeof setTimeout> | null =
    null;

  private panelOptionsChangesSub?: Subscription;
  private focusFirstOptionRetryTimeouts: ReturnType<typeof setTimeout>[] = [];
  private optionsSubscription?: Subscription;
  private panelKeyboardBinding: MatOptionListPanelBinding | null = null;

  /** Latest option list (kept in sync for async panel open). */
  resolvedOptions: SelectOption<T>[] = [];

  /**
   * After panel open, Material often returns focus to the input; while this
   * timestamp is in the future we redirect focus back to the first option.
   */

  @ContentChild('customOption', { static: false })
  customOptionTemplate: TemplateRef<any>;

  @Output() onIconClick: EventEmitter<MouseEvent> =
    new EventEmitter<MouseEvent>();
  @Output() onClick: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
  @Output() textChange = new EventEmitter<string>();
  @Output() onOptionSelected = new EventEmitter<T>();

  @Input() displayFn: (value: any) => string;
  @Input() displayErrors: boolean = true;
  @Input() debounceTime = 300;

  @Input() floatLabelType: FloatLabelType = 'auto';
  @Input() label: string;
  @Input() placeholder: string;
  @Input() fieldClass: string;
  @Input() value: string;
  @Input() type: string = 'text';
  @Input() readOnly: boolean = false;
  @Input() hasTrigger: boolean = false;
  /**
   * Whether the dialog opened by this readOnly trigger is currently open.
   * Drives `aria-expanded` for screen readers. Optional — parents that don't
   * track this can omit it; the attribute defaults to `false` in readOnly mode.
   */
  @Input() dialogOpen: boolean = false;

  @Input() options: Observable<SelectOption<T>[]>;
  @Input() initialValue: string = '';
  @Input() appearance: MatFormFieldAppearance = 'fill';

  @Input() labelClass: string;
  @Input() inputClass: string;

  @Input() inputIconClass: string;
  @Input() inputIcon: IconType;
  /** Accessibility: aria-label for the suffix icon button (e.g. Clear, Close). */
  @Input() iconButtonAriaLabel: string;

  @Input() prefixIconClass: string;
  @Input() prefixIcon: IconType;

  /** When set, forwarded to the native input (e.g. -1 to keep map overlay search out of tab order). */
  @Input() inputTabIndex?: number;

  inputValue = new Subject<string>();
  subscriptions: Subscription[] = [];
  formControl: FormControl;
  /** When readOnly and value is an object, show this instead of raw value to avoid [object Object]. */
  displayValue = '';

  /** Unique id for error message (aria-describedby link). */
  errorId = 'mot-autocomplete-err-' + Math.random().toString(36).slice(2, 11);

  /** Stable id for aria-controls → mat-autocomplete overlay panel. */
  readonly autocompletePanelId =
    'mot-autocomplete-panel-' + Math.random().toString(36).slice(2, 11);

  direction: Direction;

  get isAutocompletePanelOpen(): boolean {
    return !!this.autocompleteTrigger?.panelOpen;
  }

  /**
   * Read-only fields use [value] + no matInput FormControl, so mat-form-field never sees a "value" → label must stay floated.
   * Object-valued autocompletes (e.g. Stop) can also fail the empty check until display syncs — keep label up when value is set.
   */
  get resolvedFloatLabel(): FloatLabelType {
    if (this.readOnly) {
      return 'always';
    }
    const v = this.formControl?.value;
    if (v != null && v !== '' && typeof v === 'object') {
      return 'always';
    }
    return this.floatLabelType;
  }

  /**
   * Accessible name for the read-only dialog-trigger input ("button").
   *
   * Why this exists:
   *   With role="button" on the input, the floating <mat-label> doesn't reliably become the
   *   button's accessible name, and the input's `value` attribute is NOT announced as part
   *   of the button name. As a result, screen readers would say "Origin, button, has popup
   *   dialog" without revealing the currently selected address.
   *
   * Strategy:
   *   Compose an explicit aria-label that joins the field's label with its current
   *   display value (e.g. "Origin: Tel Aviv-Yafo"). Falls back to label-only when no value
   *   is selected, and returns null for non-readOnly fields so the editable combobox path
   *   keeps its native label/placeholder semantics.
   */
  get readOnlyAriaLabel(): string | null {
    if (!this.readOnly) {
      return null;
    }
    const label = (this.label ?? '').trim();
    const value = (this.displayValue ?? '').trim();
    if (label && value) {
      return `${label}: ${value}`;
    }
    return label || value || null;
  }

  /**
   * MatAutocompleteTrigger calls displayWith for typed strings and for writeValue.
   * Consumer displayFn often assumes an object (e.g. Stop) — return primitives as-is.
   */
  wrappedDisplayWith = (value: unknown): string => this.formatValueForInput(value);

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private readonly languageService: LanguageService
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    if (this.ngControl) {
      this.formControl = this.ngControl.control as FormControl;
      if (this.value) {
        this.formControl.setValue(this.value);
      }
      if (this.readOnly) {
        this.updateDisplayValue(this.formControl.value);
        this.subscriptions.push(
          this.formControl.valueChanges.subscribe((v) => this.updateDisplayValue(v))
        );
      }
    }

    const subscription = this.inputValue
      .pipe(debounceTime(this.debounceTime), distinctUntilChanged())
      .subscribe((currentValue) => {
        this.textChange.emit(currentValue);
      });
    this.subscriptions.push(subscription);

    // Initialize direction from language service
    this.direction = this.languageService.getSelectedLanguage().direction;

    // Subscribe to language changes
    const langSubscription = this.languageService.selectedLanguage$.subscribe(
      (language) => {
        this.direction = language.direction;
      }
    );
    this.subscriptions.push(langSubscription);

    if (this.options) {
      this.subscribeToOptions();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && !changes['options'].firstChange) {
      this.subscribeToOptions();
    }
  }

  private subscribeToOptions(): void {
    this.optionsSubscription?.unsubscribe();
    if (this.options) {
      this.optionsSubscription = this.options.subscribe((opts) => {
        this.resolvedOptions = opts ?? [];
        if (!this.readOnly && this.autocompleteTrigger?.panelOpen) {
          this.scheduleActivateFirstAutocompleteOption(0, false);
        }
      });
    } else {
      this.resolvedOptions = [];
    }
  }

  trackOption(index: number, opt: SelectOption<T>): string {
    const value = opt?.value as { id?: string | number; stopcode?: string } | null;
    const key = value?.id ?? value?.stopcode ?? opt?.label;
    return key != null ? `${key}_${index}` : `${index}`;
  }

  ngAfterViewInit(): void {
    this.hookMatTriggerToFormIfReady();
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    this.value = value;
    if (this.readOnly) {
      this.updateDisplayValue(value);
      return;
    }
    Promise.resolve().then(() => {
      if (this.autocompleteTrigger) {
        this.autocompleteTrigger.writeValue(value);
      } else {
        const el = this.motAutocompleteInput?.nativeElement;
        const text = this.formatValueForInput(value);
        if (el && el.value !== text) {
          el.value = text;
        }
      }
    });
  }

  private updateDisplayValue(value: any): void {
    if (value == null || value === '') {
      this.displayValue = '';
      return;
    }
    if (this.displayFn) {
      this.displayValue = this.displayFn(value) ?? '';
      return;
    }
    this.displayValue = typeof value === 'object' ? '' : String(value);
  }

  private formatValueForInput(value: unknown): string {
    if (value == null || value === '') {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number') {
      return String(value);
    }
    if (this.displayFn) {
      try {
        return this.displayFn(value) ?? '';
      } catch {
        return '';
      }
    }
    return typeof value === 'object' ? '' : String(value);
  }

  /** Single FormControl lives on the host CVA; wire Material trigger to the same callbacks (no inner [formControl]). */
  private hookMatTriggerToFormIfReady(): void {
    if (this.readOnly || !this.autocompleteTrigger) {
      return;
    }
    this.autocompleteTrigger.registerOnChange((v: any) => this.onChange(v));
    this.autocompleteTrigger.registerOnTouched(() => this.onTouched());
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
    this.hookMatTriggerToFormIfReady();
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
    this.hookMatTriggerToFormIfReady();
  }

  setDisabledState(isDisabled: boolean): void {
    const el = this.motAutocompleteInput?.nativeElement;
    if (el) {
      el.disabled = isDisabled;
    }
    if (!!this.formControl) {
      if (isDisabled && !this.formControl.disabled) this.formControl.disable();
      if (!isDisabled && !!this.formControl.disabled) this.formControl.enable();
    }
  }

  onInput(e: any) {
    this.inputValue.next(e.target.value);
  }

  /** preventDefault only for read-only triggers; editable fields keep native click/focus behavior. */
  onMatFormFieldClick(event: MouseEvent): void {
    if (this.readOnly) {
      event.preventDefault();
    }
    this.onClick.emit(event);
  }

  /**
   * Enter/Space open the read-only dialog (parity with mouse click).
   * Editable autocompletes keep native keyboard behavior — do not intercept.
   * Space ' ' and 'Spacebar' (legacy) are both handled.
   *
   * a11y: the inner `<input>` no longer carries the native `readonly` attribute
   * (audit: SR was announcing "read-only" on a field that's actually fillable
   * via the dialog). To compensate, swallow any printable / value-mutating key
   * so an AT user who lands here and starts typing can't leave stray text in
   * the input's `.value`. We keep Tab/Shift+Tab, arrows, modifier-only events,
   * Escape, etc. untouched so focus and SR navigation still work.
   */
  /**
   * When the autocomplete panel is open, Tab / Shift+Tab select the highlighted option,
   * close the panel, and move focus to the next / previous tab stop.
   */
  onEditableAutocompleteKeydown(event: KeyboardEvent): void {
    if (this.readOnly || event.key !== 'Tab') {
      return;
    }
    if (!this.isAutocompletePanelOpen) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.commitSelectionAndMoveTabFocus(event.shiftKey ? 'backward' : 'forward');
  }

  onAutocompleteInputKeydown(event: KeyboardEvent): void {
    if (!this.readOnly) {
      return;
    }
    const isActivationKey =
      event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar';
    if (isActivationKey) {
      this.onTriggerKeydown(event);
      return;
    }
    if (this.isValueMutatingKey(event)) {
      event.preventDefault();
    }
  }

  /**
   * True for keys that would otherwise edit the input's value (printable chars,
   * Backspace, Delete, Cut/Paste shortcuts). False for navigation/modifier
   * combinations (Tab, arrows, Home/End, Escape, copy with Ctrl+C, etc.) so
   * keyboard users can still move focus and screen readers can still read.
   */
  private isValueMutatingKey(event: KeyboardEvent): boolean {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      return true;
    }
    if ((event.ctrlKey || event.metaKey) && !event.altKey) {
      const k = event.key.toLowerCase();
      if (k === 'v' || k === 'x') {
        return true;
      }
      return false;
    }
    return event.key.length === 1;
  }

  /** When readOnly, open panel on Enter/Space (keyboard parity with click). */
  onTriggerKeydown(e: Event): void {
    e.preventDefault();
    e.stopPropagation();
    this.onClick.emit(e as any);
  }

  getFirstError(): string {
    const errors = this.formControl.errors;

    return errors[Object.keys(errors)[0]];
  }

  ngOnDestroy() {
    this.onAutocompletePanelClosed();
    this.clearFocusFirstOptionRetries();
    this.detachAutocompletePanelKeyboard();
    this.optionsSubscription?.unsubscribe();
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  handleSelectionChange(evt: MatOptionSelectionChange<T>) {
    if (!!evt.isUserInput) {
      this.onOptionSelected.emit(evt.source.value);
    }
  }

  onAutocompletePanelOpened(): void {
    if (this.readOnly) {
      return;
    }

    this.dispatchOverlayPanelOpened();

    this.panelOptionsChangesSub?.unsubscribe();
    const optionsList = this.matAutocomplete?.options;
    if (optionsList) {
      this.panelOptionsChangesSub = optionsList.changes.subscribe(() => {
        if (this.autocompleteTrigger?.panelOpen) {
          this.scheduleActivateFirstAutocompleteOption(0, false);
        }
      });
    }

    this.scheduleActivateFirstAutocompleteOption(0, false);
  }

  onAutocompletePanelClosed(): void {
    this.dispatchOverlayPanelClosed();
    this.panelOptionsChangesSub?.unsubscribe();
    this.panelOptionsChangesSub = undefined;
    this.clearFocusFirstOptionRetries();
    this.detachAutocompletePanelKeyboard();

    if (this.activateFirstOptionTimeout) {
      clearTimeout(this.activateFirstOptionTimeout);
      this.activateFirstOptionTimeout = null;
    }
  }

  private clearFocusFirstOptionRetries(): void {
    this.focusFirstOptionRetryTimeouts.forEach((id) => clearTimeout(id));
    this.focusFirstOptionRetryTimeouts = [];
  }

  private scheduleActivateFirstAutocompleteOption(
    attempt: number,
    moveDomFocusToOption: boolean
  ): void {
    if (this.activateFirstOptionTimeout) {
      clearTimeout(this.activateFirstOptionTimeout);
      this.activateFirstOptionTimeout = null;
    }

    const delayMs = attempt === 0 ? 0 : 50;
    const maxAttempts = 15;

    this.activateFirstOptionTimeout = setTimeout(() => {
      this.activateFirstOptionTimeout = null;

      if (!this.autocompleteTrigger?.panelOpen) {
        return;
      }

      const options = this.matAutocomplete?.options;
      if (!options?.length) {
        if (attempt < maxAttempts) {
          this.scheduleActivateFirstAutocompleteOption(
            attempt + 1,
            moveDomFocusToOption
          );
        }
        return;
      }

      this.applyFirstActiveAutocompleteOption(moveDomFocusToOption);
    }, delayMs);
  }

  /**
   * Highlight the first option via Material's key manager. When the panel just
   * opened, move DOM focus into the list for screen readers; while the user is
   * still typing (options refresh), keep focus on the input.
   */
  private applyFirstActiveAutocompleteOption(moveDomFocusToOption: boolean): void {
    const trigger = this.autocompleteTrigger;
    const autocomplete = this.matAutocomplete;
    if (!trigger?.panelOpen || !autocomplete?.options?.length) {
      return;
    }

    const optionsArray = autocomplete.options.toArray();
    const firstIndex = optionsArray.findIndex((o) => !o.disabled);
    if (firstIndex < 0) {
      return;
    }

    const autocompleteInternals = autocomplete as unknown as {
      _keyManager?: { setActiveItem: (index: number) => void };
    };
    const triggerInternals = trigger as unknown as {
      _scrollToOption?: (index: number) => void;
    };

    autocompleteInternals._keyManager?.setActiveItem(firstIndex);
    triggerInternals._scrollToOption?.(firstIndex);

    const input = this.motAutocompleteInput?.nativeElement;
    input?.setAttribute('aria-expanded', 'true');

    if (moveDomFocusToOption) {
      focusMatOptionElement(optionsArray[firstIndex]);
      this.attachAutocompletePanelKeyboard();
    } else {
      this.detachAutocompletePanelKeyboard();
      input?.focus({ preventScroll: true });
    }
  }

  private getAutocompletePanelElement(): HTMLElement | null {
    const triggerInternals = this.autocompleteTrigger as unknown as {
      _overlayRef?: { overlayElement: HTMLElement };
    };
    const fromOverlay = triggerInternals._overlayRef?.overlayElement;
    if (fromOverlay) {
      return fromOverlay;
    }
    if (typeof document === 'undefined') {
      return null;
    }
    return document.getElementById(this.autocompletePanelId);
  }

  private attachAutocompletePanelKeyboard(): void {
    this.detachAutocompletePanelKeyboard();
    const panel = this.getAutocompletePanelElement();
    if (!panel || !this.matAutocomplete) {
      return;
    }

    this.panelKeyboardBinding = attachMatOptionListPanelKeyboard(
      panel,
      () => this.matAutocomplete?.options?.toArray() ?? [],
      (index) => {
        const autocompleteInternals = this.matAutocomplete as unknown as {
          _keyManager?: { setActiveItem: (index: number) => void };
        };
        const triggerInternals = this.autocompleteTrigger as unknown as {
          _scrollToOption?: (index: number) => void;
        };
        autocompleteInternals._keyManager?.setActiveItem(index);
        triggerInternals._scrollToOption?.(index);
      },
      (direction) => this.commitSelectionAndMoveTabFocus(direction)
    );
  }

  private commitSelectionAndMoveTabFocus(
    direction: 'forward' | 'backward'
  ): void {
    this.selectHighlightedOptionAndClosePanel();
    const input = this.motAutocompleteInput?.nativeElement;
    if (input) {
      queueMicrotask(() => this.moveTabFocus(input, direction));
    }
  }

  /** Commit the highlighted mat-option and close the autocomplete panel. */
  private selectHighlightedOptionAndClosePanel(): void {
    const trigger = this.autocompleteTrigger;
    const autocomplete = this.matAutocomplete;
    if (!trigger?.panelOpen || !autocomplete?.options?.length) {
      trigger?.closePanel();
      return;
    }

    const options = autocomplete.options.toArray();
    const keyManager = (
      autocomplete as unknown as {
        _keyManager?: { activeItemIndex: number; activeItem: MatOption | null };
      }
    )._keyManager;

    let activeIndex = getFocusedMatOptionIndex(options);
    if (activeIndex < 0) {
      activeIndex = keyManager?.activeItemIndex ?? -1;
    }
    if (activeIndex < 0) {
      activeIndex = options.findIndex((o) => !o.disabled);
    }

    const option = options[activeIndex];
    if (option && !option.disabled) {
      const selectable = option as unknown as {
        selectViaInteraction?: () => void;
        select: () => void;
      };
      if (typeof selectable.selectViaInteraction === 'function') {
        selectable.selectViaInteraction();
      } else {
        selectable.select();
      }
    }

    trigger.closePanel();
    this.onTouched();
  }

  private moveTabFocus(
    from: HTMLElement,
    direction: 'forward' | 'backward'
  ): void {
    if (typeof document === 'undefined') {
      return;
    }
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const tabbables = Array.from(
      document.querySelectorAll<HTMLElement>(selector)
    ).filter(
      (el) =>
        !el.hasAttribute('disabled') &&
        el.tabIndex !== -1 &&
        (el.offsetWidth > 0 || el.offsetHeight > 0 || el === from)
    );
    const index = tabbables.indexOf(from);
    if (index < 0) {
      return;
    }
    const nextIndex = direction === 'forward' ? index + 1 : index - 1;
    if (nextIndex >= 0 && nextIndex < tabbables.length) {
      tabbables[nextIndex].focus();
    }
  }

  private detachAutocompletePanelKeyboard(): void {
    detachMatOptionListPanelKeyboard(this.panelKeyboardBinding);
    this.panelKeyboardBinding = null;
  }

  private dispatchOverlayPanelOpened(): void {
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent(MOT_OVERLAY_PANEL_OPENED));
    }
  }

  private dispatchOverlayPanelClosed(): void {
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent(MOT_OVERLAY_PANEL_CLOSED));
    }
  }

  /** Move keyboard/screen-reader focus to the field (e.g. after revealing a stop row). */
  focusInput(): void {
    const el = this.motAutocompleteInput?.nativeElement;
    if (el && !el.disabled) {
      el.focus();
    }
  }
}
